import React from 'react';

// Utility to handle chunk loading errors
export const lazyWithRetry = (componentImport) => {
  return new Promise((resolve, reject) => {
    // Track if we've already tried to reload
    const hasRefreshed = JSON.parse(
      window.sessionStorage.getItem('chunk_retry') || 'false'
    );

    componentImport()
      .then((component) => {
        // Clear the refresh flag on successful load
        window.sessionStorage.setItem('chunk_retry', 'false');
        resolve(component);
      })
      .catch((error) => {
        if (!hasRefreshed && error.name === 'ChunkLoadError') {
          // Set flag to prevent infinite refresh loop
          window.sessionStorage.setItem('chunk_retry', 'true');
          console.log('Chunk load error detected, refreshing page...');
          // Force reload to get fresh chunks
          return window.location.reload();
        }
        // If we've already refreshed once, or it's a different error, reject
        window.sessionStorage.setItem('chunk_retry', 'false');
        reject(error);
      });
  });
};

// Error boundary component for handling chunk errors
export class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Check if it's a chunk load error
    if (error.name === 'ChunkLoadError' ||
        (error.message && error.message.includes('Loading chunk'))) {
      // Try to recover by reloading
      const hasRefreshed = JSON.parse(
        window.sessionStorage.getItem('chunk_error_retry') || 'false'
      );

      if (!hasRefreshed) {
        window.sessionStorage.setItem('chunk_error_retry', 'true');
        window.location.reload();
        return null;
      }
      window.sessionStorage.setItem('chunk_error_retry', 'false');
    }

    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ChunkErrorBoundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full bg-white shadow rounded-lg p-6">
            <div className="text-center">
              <svg className="mx-auto h-12 w-12 text-red-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Application Update Required
              </h2>
              <p className="text-gray-600 mb-4">
                The application has been updated. Please refresh the page to get the latest version.
              </p>
              <button
                onClick={() => {
                  window.sessionStorage.removeItem('chunk_error_retry');
                  window.sessionStorage.removeItem('chunk_retry');
                  window.location.reload();
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}